import { useDocumentTitleEntity } from '@components/DocumentTitle'
import { extractSectionFromPath } from '@supplement-bacon/react-toolkit/utils'
import {
  Button,
  Card,
  Flex,
  Layout,
  Space,
  Tabs,
  TabsProps,
  Tag,
  Typography,
} from 'antd'
import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { LayoutFooter } from '../../components/LayoutFooter'
import MainHeader from '../../components/MainHeader'

import {
  ApiOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Content } from 'antd/es/layout/layout'
import dayjs from 'dayjs'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { resetEventParticipationQueryAndIds } from '../../../store/membership/EventParticipationReducer'
import {
  getEventWithId,
  selectEventById,
  setSelectedEvent,
} from '../../../store/membership/EventReducer'

const { Title, Text } = Typography

export const EventDetailLayout: React.FC = () => {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const dispatch = useAppDispatch()
  const event = useAppSelector(selectEventById(Number(eventId)))
  useDocumentTitleEntity(event?.fr?.title)
  const { isOpen } = useSBRM()

  useEffect(() => {
    setSelectedKey(extractSectionFromPath(pathname, 4))
    dispatch(resetEventParticipationQueryAndIds(initialQuery))
  }, [pathname])

  const [selectedKey, setSelectedKey] = useState<string>('generals')

  const items: TabsProps['items'] = [
    {
      key: 'informations',
      label: 'Informations',
    },
    {
      key: 'participants',
      label: 'Participants',
    },
    {
      key: 'booking',
      label: 'Booking',
    },
    {
      key: 'analytics',
      label: 'Analytics',
    },
    {
      key: 'settings',
      label: 'Settings',
    },
  ]
  const fetchEvent = () => {
    if (!eventId) {
      navigate('/list')
      return
    }
    dispatch(getEventWithId(Number(eventId)))
      .then(() => {
        dispatch(setSelectedEvent(Number(eventId)))
      })
      .catch(() => {
        navigate('/')
      })
  }

  useEffect(() => {
    if (event) {
      dispatch(setSelectedEvent(event))
    }
  }, [event])

  useEffect(() => {
    fetchEvent()
  }, [dispatch, eventId])

  useEffect(() => {
    if (event === undefined || isOpen) return
    fetchEvent()
  }, [isOpen])

  if (!event) {
    return <></>
  }

  const textStyle: React.CSSProperties = {
    color: '#999',
    fontSize: 12,
    fontWeight: 'bold',
    lineHeight: 1,
    textAlign: 'center',
    textTransform: 'uppercase',
  }

  return (
    <Layout>
      <MainHeader />
      <Layout style={{ minHeight: '100vh', marginTop: 80 }}>
        <Content>
          <Flex vertical gap={15} className="container-row mt-0b">
            <Space orientation="vertical" className="w-full" size={10}>
              <Card
                style={{
                  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.5)), url(${event.image.url})`,
                  backgroundPosition: 'center',
                  backgroundSize: 'cover',
                  minHeight: 250,
                }}
              >
                <Flex
                  style={{ position: 'absolute', right: 10, top: 20 }}
                  gap={10}
                >
                  <Tag color="blue" icon={<ApiOutlined />}>
                    {event.bookingMethod === 'in-app'
                      ? 'In app booking'
                      : 'Chat request'}
                  </Tag>
                  <Tag
                    color={event.isPublished ? 'green' : 'orange'}
                    icon={
                      event.isPublished ? (
                        <EyeOutlined />
                      ) : (
                        <EyeInvisibleOutlined />
                      )
                    }
                  >
                    {event.isPublished ? 'Published' : 'Unpublished'}
                  </Tag>
                </Flex>
                <Space
                  style={{ position: 'absolute', left: 20, bottom: 20 }}
                  size={20}
                >
                  <Space
                    orientation="vertical"
                    size={0}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: 5,
                      lineHeight: 1,
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: 60,
                      height: 60,
                    }}
                  >
                    <Text style={textStyle}>
                      {dayjs(event.start).format('ddd')}
                    </Text>
                    <Text style={{ ...textStyle, fontSize: 16 }}>
                      {dayjs(event.start).format('DD')}
                    </Text>
                    <Text style={textStyle}>
                      {dayjs(event.start).format('MMM')}
                    </Text>
                  </Space>

                  <Title className="m-0" level={4} style={{ color: 'white' }}>
                    {event.fr.title}
                  </Title>
                </Space>
              </Card>
              <Space className="w-full space-between align-center">
                <Tabs
                  activeKey={selectedKey}
                  items={items}
                  onChange={(key) => {
                    setSelectedKey(key)
                    navigate(key)
                  }}
                  tabBarStyle={{ marginBottom: 0 }}
                />
                <Space>
                  <SBRM.Actions
                    entity={Entity.event}
                    entityId={event.id}
                    actions={[SBRMAction.Update]}
                    trigger={
                      <Button type="primary" icon={<EditOutlined />}>
                        Edit
                      </Button>
                    }
                  />
                </Space>
              </Space>
            </Space>
            <Flex vertical>
              <Outlet />
            </Flex>
          </Flex>
        </Content>
        <LayoutFooter />
      </Layout>
    </Layout>
  )
}
